import { isEqual, isNil, mapValues, omit, omitBy } from 'lodash'
import {
  AdCreative,
  AdCreativeType,
  CALLS_CALL_TO_ACTION,
  CreativeCopyPatch,
  CreativeCopySlidePatch,
  FBCallToAction,
  LEAD_FORM_CALLS_TO_ACTION,
  PlacementBucket,
} from '@marketing-milk/interfaces'
import { LocalAdPreviewCreative } from 'components/ad-preview'
import { isDynamicCreative } from '../../campaigns/helpers/campaign.helpers'
import { DynamicCombination, DynamicPools, pinnedCombination } from './dynamicCombination'
import { DraftCard, DraftMedia, previewSingleMedia } from './mediaDraft'

/**
 * The copy an employee is editing on the review surface, before they save it.
 *
 * The draft IS the request body — `CreativeCopyPatch`, the schema the server
 * validates against — so there is no draft-to-payload mapping step that could
 * drop or rename a field on the way out. Every field the ad has is filled in from
 * the saved creative, because the fields are on screen: a blank one means "the ad
 * has no description", not "leave the description alone". A field the ad has
 * never had is left out instead — see `SAVED_TEXT`.
 */
export function copyDraftFromCreative(creative: AdCreative): CreativeCopyPatch {
  const draft: CreativeCopyPatch = {}
  for (const field of TEXT_FIELDS) {
    const saved = SAVED_TEXT[field](creative)
    if (saved !== undefined) draft[field] = saved
  }
  // The lead form is left out until someone picks one: the server keeps the ad's
  // own when it is absent, and re-sending it would re-check a form that may since
  // have moved business and refuse an unrelated edit.

  // A CTA is a choice from a fixed list, and an ad can genuinely have none.
  // Absence is the initial no-button state; an explicit null is reserved for a
  // user clearing a CTA that was previously present.
  if (creative.callToAction) draft.callToAction = creative.callToAction

  if (creative.format === AdCreativeType.carousel) {
    draft.slides = (creative.carouselSlides ?? [])
      .filter(slide => slide.id !== undefined)
      .map(
        // As with the ad's own text, a field the card has never had stays out.
        (slide): CreativeCopySlidePatch =>
          omitBy(
            {
              id: slide.id!,
              headline: slide.linkHeadline,
              description: slide.linkDescription,
              url: slide.linkWebsiteURL,
            },
            isNil
          ) as CreativeCopySlidePatch
      )
  }

  if (isDynamicCreative(creative)) {
    draft.pools = {
      primaryTexts: creative.dynamicConfig.primaryTexts ?? [],
      headlines: creative.dynamicConfig.headlines ?? [],
      descriptions: creative.dynamicConfig.descriptions ?? [],
      callToActions: creative.dynamicConfig.callToActions ?? [],
    }
  }

  return draft
}

/** The ad-level text fields, by their patch names. */
export type CopyTextField =
  | 'name'
  | 'graphicText'
  | 'primaryText'
  | 'headline'
  | 'description'
  | 'url'
  | 'displayLink'
  | 'callPhoneNumber'

/**
 * Each text field as the saved ad has it, or undefined when it has none.
 *
 * A missing one stays out of the draft rather than being seeded as `''`: the
 * server stores it as null and reads `''` as a different value, so sending it
 * back untouched would record a change — and lift the client's approval — on a
 * save that changed nothing.
 */
const SAVED_TEXT: Record<CopyTextField, (creative: AdCreative) => string | undefined> = {
  name: creative => creative.name,
  graphicText: creative => creative.graphicText,
  primaryText: creative => creative.primaryText,
  headline: creative => creative.linkHeadline,
  description: creative => creative.linkDescription,
  // The server refuses an empty destination, so an ad saved without one — a blank
  // ad made before the business had a website — can't be sent one back.
  url: creative => creative.linkWebsiteURL || undefined,
  displayLink: creative => creative.linkDisplayURL,
  // Only a Call Now ad keeps a number; one sent behind any other button is
  // recorded as cleared.
  callPhoneNumber: creative =>
    creative.callToAction === CALLS_CALL_TO_ACTION ? creative.callPhoneNumber : undefined,
}

const TEXT_FIELDS = Object.keys(SAVED_TEXT) as CopyTextField[]

/** A typed value, left out again when it is back to the nothing the ad was saved with. */
export function withText(
  draft: CreativeCopyPatch,
  creative: AdCreative,
  field: CopyTextField,
  value: string
): CreativeCopyPatch {
  if (value === '' && SAVED_TEXT[field](creative) === undefined) return omit(draft, field)
  return { ...draft, [field]: value }
}

/**
 * The ad's own button. A number only rides along behind Call Now — the server
 * drops it behind any other — and coming back to Call Now brings back the one the
 * ad was saved with.
 */
export function withCallToAction(
  draft: CreativeCopyPatch,
  creative: AdCreative,
  callToAction: FBCallToAction | null
): CreativeCopyPatch {
  if (callToAction !== CALLS_CALL_TO_ACTION)
    return { ...omit(draft, 'callPhoneNumber'), callToAction }
  const saved = draft.callPhoneNumber ?? creative.callPhoneNumber
  return saved === undefined
    ? { ...draft, callToAction }
    : { ...draft, callToAction, callPhoneNumber: saved }
}

/**
 * Attach a lead form, or detach it with null. A form rides on the button, so
 * attaching one moves any button that can't open it to Apply Now, and trims a dynamic ad's button pool to the ones that can, keeping at
 * least one.
 */
export function withLeadCard(
  draft: CreativeCopyPatch,
  creative: AdCreative,
  leadCardID: number | null
): CreativeCopyPatch {
  if (leadCardID === (creative.leadCardUsed?.id ?? null)) return omit(draft, 'leadCardID')
  if (leadCardID === null) return { ...draft, leadCardID: null }
  const next = { ...draft, leadCardID }
  if (isDynamicCreative(creative)) {
    const legal = (draft.pools?.callToActions ?? []).filter(isLeadFormCallToAction)
    const callToActions = legal.length ? legal : [FBCallToAction.APPLY_NOW]
    return isEqual(callToActions, draft.pools?.callToActions)
      ? next
      : { ...next, pools: { ...draft.pools, callToActions } }
  }
  return isLeadFormCallToAction(draft.callToAction)
    ? next
    : withCallToAction(next, creative, FBCallToAction.APPLY_NOW)
}

const isLeadFormCallToAction = (cta?: FBCallToAction | null): boolean =>
  !!cta && LEAD_FORM_CALLS_TO_ACTION.includes(cta)

/** The lead form the draft leaves the ad with, by id. */
export const draftLeadCardID = (
  draft: CreativeCopyPatch,
  creative: AdCreative
): number | undefined =>
  draft.leadCardID === undefined ? creative.leadCardUsed?.id : draft.leadCardID ?? undefined

/**
 * The pools as the draft currently has them, so the picker lists what is typed
 * and the preview assembles from it. `assets` comes in already drafted — the
 * media half is staged as operations, not as pool text.
 */
export function draftPools(
  saved: DynamicPools,
  draft: CreativeCopyPatch,
  assets: DraftMedia[]
): DynamicPools {
  const pools = draft.pools
  if (!pools) return { ...saved, assets }
  return {
    assets,
    primaryTexts: pools.primaryTexts ?? [],
    headlines: pools.headlines ?? [],
    descriptions: pools.descriptions ?? [],
    callToActions: (pools.callToActions ?? []) as FBCallToAction[],
  }
}

/** The media the editor is showing, derived once and shared with the preview. */
export interface DraftMediaView {
  single?: DraftMedia
  /** Per-placement only. */
  cuts: Partial<Record<PlacementBucket, DraftMedia>>
  cards: DraftCard[]
  /** Already drafted, assets included. */
  pools: DynamicPools
}

/**
 * The ad as the draft would render it, in the shape the local renderer reads —
 * which is what lets the preview redraw as someone types instead of waiting on a
 * save and then on Facebook.
 */
export function copyDraftToPreview(
  creative: AdCreative,
  draft: CreativeCopyPatch,
  combination: DynamicCombination,
  media: DraftMediaView
): LocalAdPreviewCreative {
  if (isDynamicCreative(creative)) {
    // The pinned-combination resolver already merges pools down to one rendering;
    // hand it the drafted pools rather than growing a second merge here.
    return pinnedCombination(
      { ...creative, ...draftedCreativeFields(creative, draft) },
      media.pools,
      combination
    )
  }

  const base = { ...creative, ...draftedCreativeFields(creative, draft) }

  if (creative.format === AdCreativeType.carousel) {
    return {
      ...base,
      // The cards the editor is showing, media and all — the same derivation, so
      // the preview cannot disagree with the tiles beside it.
      carouselSlides: media.cards.map(card => ({
        linkHeadline: card.headline,
        linkDescription: card.description,
        linkWebsiteURL: card.url,
        ...previewMedia(card.media),
      })),
    }
  }

  if (creative.format === AdCreativeType.per_placement) {
    return {
      ...base,
      placementCuts: mapValues(media.cuts, cut => cut && previewAsset(cut)),
    }
  }

  // The renderer branches on `format`, so a staged image↔video swap has to move
  // it. With no media resolved at all the ad is left as it was, rather than being
  // called an image ad because nothing was found.
  const single = media.single
  if (!single) return base
  return { ...base, singleImage: undefined, thumbnailURL: undefined, ...previewSingleMedia(single) }
}

/** What the media the editor is showing looks like to the local renderer. */
function previewMedia(media?: DraftMedia) {
  return media ? previewAsset(media) : {}
}

function previewAsset(media: DraftMedia) {
  // `''` rather than absent: the renderer falls back to its placeholder on a
  // falsy URL, and `AdImage.url` is not optional.
  if (media.mediaType === 'image') {
    return { mediaType: 'image' as const, image: { url: media.url ?? '' } }
  }
  return {
    mediaType: 'video' as const,
    video: {
      source: media.source,
      storedSource: media.storedSource,
      thumbnail: media.thumbnail,
      animatedImage: media.animatedImage,
    },
  }
}

/** The creative-level fields a draft overrides, spread over a saved creative. */
function draftedCreativeFields(
  creative: AdCreative,
  draft: CreativeCopyPatch
): Partial<AdCreative> {
  return {
    primaryText: draft.primaryText ?? creative.primaryText,
    linkHeadline: draft.headline ?? creative.linkHeadline,
    linkDescription: draft.description ?? creative.linkDescription,
    callToAction:
      draft.callToAction === null ? undefined : draft.callToAction ?? creative.callToAction,
    linkWebsiteURL: draft.url ?? creative.linkWebsiteURL,
    linkDisplayURL: draft.displayLink ?? creative.linkDisplayURL,
  }
}
